import _get from 'lodash/get';
import { helpingMethod } from '../../../utils/HelpingMethod';
import { DeliveriesListResponse } from './deliveryApis';
import { MetaOption, LubricationChartProduct } from '../../../types';

interface DeliveryItem {
  id: string | number;
  srno: number;
  vessel: string;
  vesselId?: number; // Store vesselId for API calls in edit mode
  manager: string;
  port: string;
  date: string;
  deliveryDate?: string; // Raw deliveryDate from backend for edit mode
  supplier: string;
  lub_costs: string | number;
  ex_costs: string | number;
  ex_costs_fifo: number;
  type: string;
  deliveryType: string; // Raw delivery type value (standard, adjusting, secondHand, newBuilding)
  invoiced: string;
  remarks: string;
}

interface DeliveryApiParser {
  DeliveriesListParser: (_res: any) => DeliveriesListResponse;
  ProductsByVesselParser: (_res: any) => DeliveryProduct[];
  DeliveryProductsParser: (_res: any) => DeliveryProduct[]; // For edit mode - includes prefilled values
  InactiveProductsByVesselParser: (_res: any) => LubricationChartProduct[];
  ActiveProductsFromLubricantParser: (_res: any) => LubricationChartProduct[];
  suppliersParser: (_res: any) => MetaOption[];
  managerFilterOptionsParser: (_res: any) => MetaOption[];
  portFilterOptionsParser: (_res: any) => MetaOption[];
  vesselFilterOptionsParser: (_res: any) => MetaOption[];
  supplierFilterOptionsParser: (_res: any) => MetaOption[];
  deliveryTypesFilterOptionsParser: (_res: any) => string[];
}

const deliveryApiParser: DeliveryApiParser = {
  DeliveriesListParser: (_res: any): DeliveriesListResponse => {
    if (!_res) return { data: [], pagination: 0 };

    // Helper function to normalize backend delivery type to internal format
    const normalizeDeliveryType = (backendType: string): string => {
      if (!backendType) return '';
      const normalized = backendType.toUpperCase().trim();

      // Map backend values to internal values
      const typeMap: Record<string, string> = {
        ADJUSTMENT: 'Adjusting',
        ADJUSTING: 'Adjusting',
        STANDARD: 'Standard',
        SECOND_HAND_VESSEL_ACQUISITION: 'secondHand',
        SECONDHAND: 'secondHand',
        ACQUISITION: 'S&P Purchase', // Backend returns "ACQUISITION" for second hand
        NEW_BUILDING: 'New Building',
        NEWBUILDING: 'New Building',
      };

      return typeMap[normalized] || normalized.toLowerCase();
    };

    // Parsing data
    const data: DeliveryItem[] = _res?.content?.map((itm: any) => {
      const rawTypeFromBackend = _get(itm, 'deliveryType', '');
      const normalizedType = normalizeDeliveryType(rawTypeFromBackend);
      const date = _get(itm, 'deliveryDate', '');
      const formattedDate = date ? helpingMethod.formatDate(date) : '-';
      const invoiced = _get(itm, 'invoiced', '');
      const poNumber = _get(itm, 'poNumber', null);
      return {
        id: _get(itm, 'id', 0),
        srno: _get(itm, 'id', 0),
        vessel: _get(itm, 'vesselName', '-'),
        vesselId: _get(itm, 'vesselId', undefined), // Store vesselId if available from backend
        manager: _get(itm, 'managerName', '-'),
        port: _get(itm, 'portName', '-'),
        date: formattedDate,
        deliveryDate: date, // Store raw deliveryDate from backend for edit mode
        supplier: _get(itm, 'supplierName', '-'),
        lub_costs: _get(itm, 'totalLubricantCost', 0),
        ex_costs: _get(itm, 'totalExtraCost', 0),
        ex_costs_fifo: _get(itm, 'totalCost', 0),
        type: normalizedType,
        deliveryType: normalizedType, // Store normalized delivery type value (adjusting, standard, etc.) for edit mode
        invoiced: invoiced ? 'Yes' : 'No',
        invoiceNumber: _get(itm, 'invoiceNumber', '-'),
        poNumber: Number(poNumber) ? Number(poNumber) : null,
        remarks: _get(itm, 'remarks', '-'),
        extraCosts: _get(itm, 'extraCosts', []), // Include extraCosts from backend if available
      };
    });

    const paginationData = _res?.totalElements;
    return { data, pagination: paginationData };
  },

  ProductsByVesselParser: (_res: any): DeliveryProduct[] => {
    if (!_res) return [];

    // Handle the actual API response structure
    if (_res && _res.data && _res.data.content && Array.isArray(_res.data.content)) {
      const data: DeliveryProduct[] = _res.data.content.map((item: any) => ({
        id: _get(item, 'productId', 0),
        name: _get(item, 'productName', '') || '-',
        maker: _get(item, 'makerName', '') || '-',
        category: _get(item, 'categoryName', '') || '-',
        type: _get(item, 'typeName', '') || '-',
        quantity: '', // Empty initially for user input
        quantityUnit: _get(item, 'unitSymbol', '') || '-',
        netValue: '', // Empty initially for user input
        netValueUnit: 'usd', // Default currency
        packagingCost: '', // Empty initially for user input
        packagingCostUnit: 'usd', // Default currency
        isTypeIn: _get(item, 'typeIn', false),
        storageQuantity: '',
        sumpTankQuantity: '',
        unit: _get(item, 'unitSymbol', '') || '-',
      }));

      return data;
    }

    return [];
  },

  DeliveryProductsParser: (_res: any): DeliveryProduct[] => {
    if (!_res) return [];

    // Handle the actual API response structure for delivery products (with prefilled values)
    // Response structure: { success: true, data: [...], message: "...", ... }
    let productsArray: any[] = [];

    if (Array.isArray(_res)) {
      productsArray = _res;
    } else if (_res && _res.data && Array.isArray(_res.data)) {
      productsArray = _res.data;
    } else if (_res && _res.data && _res.data.content && Array.isArray(_res.data.content)) {
      productsArray = _res.data.content;
    }

    const data: DeliveryProduct[] = productsArray.map((item: any) => {
      // Handle quantity - can be storageQuantity for adjusting type or quantity for standard
      const quantity = _get(item, 'quantity', null);
      const sumpTankQuantity = _get(item, 'sumpTankQuantity', null);
      const netValue = _get(item, 'netValue', null);
      const packagingCost = _get(item, 'packagingCost', null);

      return {
        id: _get(item, 'productId', 0),
        name: _get(item, 'productName', '') || '-',
        maker: _get(item, 'makerName', '') || '-', // API returns makerName
        category: _get(item, 'productCategory', '') || '-', // API returns productCategory
        type: _get(item, 'productType', '') || '-', // API returns productType (can be null)
        quantity: quantity !== null && quantity !== undefined ? quantity.toString() : '', // Prefilled quantity
        quantityUnit: _get(item, 'uomSymbol', '') || '-', // Get unit symbol from API response
        netValue: netValue !== null && netValue !== undefined ? netValue.toString() : '', // Prefilled net value
        netValueUnit: 'usd', // Default currency
        packagingCost:
          packagingCost !== null && packagingCost !== undefined ? packagingCost.toString() : '', // Prefilled packaging cost
        packagingCostUnit: 'usd', // Default currency
        isTypeIn: _get(item, 'typeIn', false), // Parse typeIn from API response
        storageQuantity: quantity !== null && quantity !== undefined ? quantity.toString() : '', // For adjusting/secondHand, quantity maps to storageQuantity
        sumpTankQuantity:
          sumpTankQuantity !== null && sumpTankQuantity !== undefined
            ? sumpTankQuantity.toString()
            : '', // Prefilled sump tank quantity
        unit: _get(item, 'uomSymbol', '') || '-', // Get unit symbol from API response
      };
    });

    return data;
  },

  InactiveProductsByVesselParser: (_res: any): LubricationChartProduct[] => {
    if (!_res) return [];

    // Handle the actual API response structure
    if (_res && _res.data && _res.data.content && Array.isArray(_res.data.content)) {
      const data: LubricationChartProduct[] = _res.data.content.map((item: any) => ({
        id: _get(item, 'productId', 0),
        productName: _get(item, 'productName', '') || '-',
        maker: _get(item, 'makerName', '') || '-',
        category: _get(item, 'categoryName', '') || '-',
        type: _get(item, 'typeName', '') || '-',
        unit: _get(item, 'unitSymbol', '') || '-', // Changed from unitName to unitSymbol
        status: 'Inactive', // All products from this endpoint are inactive
      }));

      return data;
    }

    return [];
  },

  ActiveProductsFromLubricantParser: (_res: any): LubricationChartProduct[] => {
    if (!_res) return [];

    // Handle the actual API response structure from lubricant products API
    if (_res && _res.data && _res.data.content && Array.isArray(_res.data.content)) {
      const data: LubricationChartProduct[] = _res.data.content
        .filter((item: any) => _get(item, 'active', false)) // Only active products
        .map((item: any) => ({
          id: _get(item, 'id', 0),
          productName: _get(item, 'name', '') || '-',
          maker: _get(item, 'makerName', '') || '-',
          category: _get(item, 'categoryName', '') || '-',
          type: _get(item, 'typeName', '') || '-',
          unit: _get(item, 'uomName', '') || '-',
          status: 'Active', // All products from this filtered list are active
        }));

      return data;
    }

    return [];
  },

  suppliersParser: (_res: any): MetaOption[] => {
    if (!_res) return [];

    // Parsing data
    const data: MetaOption[] = _res?.data?.map((itm: any) => ({
      value: String(itm.supplierId),
      label: String(itm.shortName),
    }));

    return data;
  },

  managerFilterOptionsParser: (_res: any): MetaOption[] => {
    if (!_res) return [];

    // Parsing data
    const data: MetaOption[] = _res?.data?.map((itm: any) => ({
      value: _get(itm, 'id', ''),
      label: _get(itm, 'shortName', '-'),
    }));

    return data;
  },

  portFilterOptionsParser: (_res: any): MetaOption[] => {
    if (!_res) return [];

    // Parsing data
    const data: MetaOption[] = _res?.data?.map((itm: any) => ({
      value: _get(itm, 'id', ''),
      label: _get(itm, 'name', '-'),
    }));

    return data;
  },

  vesselFilterOptionsParser: (_res: any): MetaOption[] => {
    if (!_res) return [];

    // Parsing data
    const data: MetaOption[] = _res?.data?.map((itm: any) => ({
      value: _get(itm, 'id', ''),
      label: _get(itm, 'name', '-'),
    }));

    return data;
  },

  supplierFilterOptionsParser: (_res: any): MetaOption[] => {
    if (!_res) return [];

    // Parsing data
    const data: MetaOption[] = _res?.data?.map((itm: any) => ({
      value: _get(itm, 'id', ''),
      label: _get(itm, 'shortName', '-'),
    }));

    return data;
  },

  deliveryTypesFilterOptionsParser: (_res: any): string[] => {
    if (!_res) return [];

    // Parsing data
    const data: string[] = _res?.data?.map((itm: any) => {
      if (itm?.toLowerCase() === 'adjustment') return 'Adjusting';
      return helpingMethod.formatChipLabel(itm);
    });

    return data;
  },
};

export default deliveryApiParser;
export interface DeliveryProduct {
  id: number;
  name: string;
  category: string;
  type: string;
  quantity: string;
  quantityUnit: string;
  netValue: string;
  netValueUnit: string;
  packagingCost: string;
  packagingCostUnit: string;
  isTypeIn: boolean;
  // New fields for Adjusting delivery type
  storageQuantity?: string;
  sumpTankQuantity?: string;
  unit?: string;
}

export interface Delivery {
  id: string;
  type: string;
  vessel: string;
  supplier: string;
  port: string;
  date: string | null;
  poNumber: string | null;
  invoiceNumber: string;
  salesTax: string;
  localTax: string;
  status: string;
  products: DeliveryProduct[];
  createdAt: string;
  updatedAt?: string;
}
